import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StoreService } from 'src/app/store.service';
import { UtilitiesService } from 'src/app/core/utilities.service';

@Component({
  selector: 'app-design',
  templateUrl: './design.component.html',
  styleUrls: ['./design.component.scss'],
})
export class DesignComponent implements OnInit {
  constructor(
    private svc: StoreService,
    private router: Router,
    private utils: UtilitiesService
  ) {}

  private bearer: string;
  private storeId: string;

  /** Title of the store's home page, null when it has none. */
  public homePageTitle: string | null = null;
  /** Needed to act on the page; null whenever the store has no home page. */
  public homePageId: number | null = null;
  /** Whether the home page is published, i.e. whether it is the default. */
  public homePageVisible = false;
  /** Until the lookup answers, the card cannot say whether it creates or edits. */
  public homePageLoading = true;
  /**
   * Set when the lookup could not be answered at all. Distinct from "no home page":
   * the card must not offer to create one it cannot first rule out, or a second
   * home page would be created and the existing one silently demoted.
   */
  public homePageLookupFailed = false;
  public openingHomePage = false;
  public switchingDefault = false;

  /**
   * The one country whose storefront shows the home page; '' = every country.
   * Any other country keeps the built-in storefront home page. Home pages that
   * predate this carry '', so they keep showing everywhere.
   */
  public homePageCountry = '';
  /** The store's active countries (Preferences), one option each. */
  public storeCountries: { code: string; name: string }[] = [];
  public countriesLoaded = false;
  public changingCountry = false;

  /** The store has a home page, whether or not it is the default. */
  get hasHomePage(): boolean {
    return this.homePageId !== null;
  }

  /**
   * The two cards are one choice: exactly one of them answers on the storefront's
   * home route. A home page holds it only while it is published, so that single
   * flag decides both toggles and neither can be set independently.
   */
  get homePageIsDefault(): boolean {
    return this.hasHomePage && this.homePageVisible;
  }

  get websiteIsDefault(): boolean {
    return !this.homePageIsDefault;
  }

  /** Any call in flight, or a lookup that never answered. */
  get homePageBusy(): boolean {
    return (
      this.homePageLoading ||
      this.openingHomePage ||
      this.switchingDefault ||
      this.changingCountry ||
      this.homePageLookupFailed
    );
  }

  /**
   * The page is limited to a country the store no longer sells in. No storefront
   * URL can carry that country, so the page shows nowhere -- worth saying, since
   * the picker would otherwise look like it offers every country.
   */
  get homePageCountryMissing(): boolean {
    return (
      this.countriesLoaded &&
      this.homePageCountry !== '' &&
      !this.storeCountries.some((c) => c.code === this.homePageCountry)
    );
  }

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.loadHomePage();
    this.loadStoreCountries();
  }

  /**
   * A store has at most one home page, so the card is either "create" or "edit" —
   * never a list. Which one is decided here so the button says the right thing
   * before it is clicked.
   */
  private loadHomePage(): void {
    this.svc.getHomePage(this.bearer, this.storeId).subscribe(
      (res: any) => {
        this.homePageTitle = res?.data?.page_title ?? null;
        this.homePageId = res?.data?.store_page_id ?? null;
        this.homePageVisible = +(res?.data?.visibility ?? 0) === 1;
        this.homePageCountry = String(res?.data?.country_code ?? '').toUpperCase();
        this.homePageLookupFailed = false;
        this.homePageLoading = false;
      },
      (error) => {
        // Say so on the card rather than offering a button that fails on click.
        // The usual cause is an environment whose middleware predates
        // /page/home_page: the gateway's 404 override answers with the storefront
        // shell, so this arrives as an HTML-parsed-as-JSON failure, not a 404.
        this.homePageTitle = null;
        this.homePageId = null;
        this.homePageVisible = false;
        this.homePageCountry = '';
        this.homePageLookupFailed = true;
        this.homePageLoading = false;
        console.error('getHomePage failed', error);
      }
    );
  }

  /**
   * The countries offered in the HTML Editor card: the store's active ones, the
   * same list Preferences manages. The list can carry a country twice, so it is
   * reduced to one option per code.
   */
  private loadStoreCountries(): void {
    this.svc.getStoreCountry(this.bearer, this.storeId).subscribe(
      (res: any) => {
        const byCode = new Map<string, { code: string; name: string }>();

        for (const row of res?.data ?? []) {
          const code = String(row?.country_code ?? '').toUpperCase();

          if (!code || +row?.status !== 1 || byCode.has(code)) {
            continue;
          }

          byCode.set(code, {
            code,
            name: String(row?.country_name || code).trim(),
          });
        }

        this.storeCountries = Array.from(byCode.values());
        this.countriesLoaded = true;
      },
      (error) => {
        console.error('getStoreCountry failed', error);
      }
    );
  }

  /**
   * Limit the home page to one country's storefront, or '' for every country.
   * Applied straight away, like Set as Default. The picker is moved first and
   * put back if the save fails, so it never shows a country that was not saved.
   */
  public setHomePageCountry(code: string): void {
    if (this.homePageBusy || !this.hasHomePage || code === this.homePageCountry) {
      return;
    }

    const previous = this.homePageCountry;
    this.homePageCountry = code;
    this.changingCountry = true;

    this.svc
      .setHomePageState(this.bearer, this.storeId, this.homePageId, {
        country_code: code,
      })
      .subscribe(
        () => {
          this.changingCountry = false;
          this.utils.notify.showSuccess(
            this.utils.translate('Home page country updated.')
          );
        },
        (error) => {
          this.changingCountry = false;
          this.homePageCountry = previous;
          this.utils.notify.showError(
            this.utils.translate('Unable to update the home page country.')
          );
          console.error('setHomePageState failed', error);
        }
      );
  }

  /**
   * Open the store's home page in the HTML editor, or start a new one. The lookup
   * is repeated on click rather than trusted from ngOnInit: the page may have been
   * created or deleted in another tab while this screen sat open.
   */
  public editHomePage(): void {
    if (this.homePageBusy) {
      return;
    }
    this.openingHomePage = true;

    this.svc.getHomePage(this.bearer, this.storeId).subscribe(
      (res: any) => {
        this.openingHomePage = false;
        const pageId = res?.data?.store_page_id;

        if (pageId) {
          this.router.navigate(['/dashboard/page/edit/', pageId]);
        } else {
          this.router.navigate(['/dashboard/page/create'], {
            queryParams: { type: 'home' },
          });
        }
      },
      (error) => {
        this.openingHomePage = false;
        this.homePageLookupFailed = true;
        this.utils.notify.showError(
          this.utils.translate('Unable to open the home page editor.')
        );
        console.error('getHomePage failed', error);
      }
    );
  }

  /**
   * Both Set as Default switches drive the same choice, so either one flips it and
   * neither can be set independently: the home page answers on "/" while it is
   * published, and the storefront's own home page answers whenever it is not.
   * Turning either switch off therefore turns the other on.
   *
   * The custom page is kept and stays editable either way — only what answers on
   * "/" changes — so this is always one click from being undone.
   */
  public toggleDefault(): void {
    if (this.homePageBusy || !this.hasHomePage) {
      return;
    }

    if (this.homePageIsDefault) {
      this.setVisibility(
        0,
        'Your storefront home page has been restored.',
        'Unable to turn off the custom home page.'
      );
      return;
    }

    this.setVisibility(
      1,
      'Your custom home page is live again.',
      'Unable to turn on the custom home page.'
    );
  }

  private setVisibility(
    visibility: number,
    successMsg: string,
    errorMsg: string
  ): void {
    this.switchingDefault = true;

    this.svc
      .setHomePageState(this.bearer, this.storeId, this.homePageId, {
        visibility,
      })
      .subscribe(
        () => {
          this.switchingDefault = false;
          this.homePageVisible = visibility === 1;
          this.utils.notify.showSuccess(this.utils.translate(successMsg));
        },
        (error) => {
          this.switchingDefault = false;
          this.utils.notify.showError(this.utils.translate(errorMsg));
          console.error('setHomePageState failed', error);
        }
      );
  }
}
